import { Download, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useDevelopmentFixtures } from "../../api/client";
import { Button } from "../../components/ui";
import { commerceApi, type CaseDossier } from "../../data/workspace";

/** The case has a quote the customer received (not a draft): the confirmation repeats what it agreed. */
export function hasAgreedQuote(dossier: CaseDossier) {
  return (dossier.quotes ?? []).some((quote) => quote.status !== "draft");
}

/**
 * Confirmation of the visit: the agreed quote with the payments received and the rest. It only
 * appears once money arrived, after the payment receipt.
 */
export function CaseConfirmation({ dossier, paid, conversationId, canSend }: {
  dossier: CaseDossier;
  paid: number;
  conversationId: string | null;
  canSend: boolean;
}) {
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  if (!(paid > 0) || !hasAgreedQuote(dossier)) return null;
  const label = dossier.service === "school" ? "Confirmación de la visita" : "Confirmación de la reserva";
  const run = async (key: string, task: () => Promise<unknown>, done: string) => {
    setBusy(key);
    setFeedback("");
    try {
      if (!useDevelopmentFixtures) await task();
      setFeedback(done);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "No se pudo completar.");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="payment-docs case-confirmation">
      <div className="payment-docs__row">
        <div>
          <strong>{label}</strong>
          <small>Lo acordado en la cotización, con lo pagado y lo pendiente.</small>
        </div>
        <div className="case-actions">
          <Button variant="secondary" icon={Download} disabled={!!busy} onClick={() => void run("pdf", () => commerceApi.downloadCaseConfirmation(dossier.key), "Confirmación descargada.")}>
            PDF
          </Button>
          {canSend && conversationId && (
            <Button icon={MessageCircle} aria-label={`Enviar ${label} por WhatsApp`} disabled={!!busy} onClick={() => void run("wa", () => commerceApi.sendCaseConfirmation(dossier.key, conversationId), `${label} enviada por WhatsApp.`)}>
              {busy === "wa" ? "Enviando…" : "WhatsApp"}
            </Button>
          )}
        </div>
      </div>
      {feedback && <p className="quote-feedback" role="status">{feedback}</p>}
    </div>
  );
}
